import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { 
  GraduationCap, Plus, CheckCircle, AlertTriangle, Loader2, 
  MoreVertical, Edit, History, Filter, CalendarClock, ShieldCheck,
  Download, FileSpreadsheet, FileText, FileSearch, Eye,
  Users, ListChecks, ArrowRight
} from 'lucide-react';
import DocumentExtractionReview from '../../components/documents/DocumentExtractionReview';
import TrainingHubQueueSummary from '../../components/training/TrainingHubQueueSummary';
import TrainingIntegrityMonitor from '../../components/training/TrainingIntegrityMonitor';
import { formatBackendDate, formatBackendDateTime } from '../../lib/dateUtils';
import API_BASE from '../../utils/apiBase';

const API = API_BASE;

// ═══════════════════════════════════════════════════════════════════════════════
// SINGLE SOURCE OF TRUTH: Status comes from backend ONLY
// Frontend MUST use backend-computed status (computed_status, renewal_status, status_label, status_color)
// NO local date calculations for expired/valid/renewal status
// ═══════════════════════════════════════════════════════════════════════════════

// Helper to get expiry display from backend-computed fields ONLY
const getBackendExpiryStatus = (record) => {
  // Use backend-computed fields - NO LOCAL CALCULATION
  if (record.computed_status) {
    return {
      status: record.renewal_status || record.computed_status,
      label: record.status_label || record.computed_status,
      daysText: record.days_until_expiry !== null && record.days_until_expiry !== undefined
        ? (record.days_until_expiry < 0 ? `${Math.abs(record.days_until_expiry)} days ago` : `${record.days_until_expiry} days`)
        : '',
      color: record.status_color || 'gray'
    };
  }
  
  // No computed status and no expiry = no expiry badge needed
  if (!record.expiry_date) return null;
  
  // Backend should always provide computed_status for records with expiry_date
  // If we reach here, it's a data issue - return safe default
  console.warn('Training record missing computed_status:', record.id);
  return {
    status: 'unknown',
    label: 'Status pending',
    daysText: '',
    color: 'gray'
  };
};

export default function TrainingPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [training, setTraining] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [trainingDefinitions, setTrainingDefinitions] = useState([]);
  const [matrixModel, setMatrixModel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [partialDataUnavailable, setPartialDataUnavailable] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Track if initial data has been fetched to prevent infinite loops
  const hasFetched = useRef(false);
  
  // Initialize filter from URL params
  const [filter, setFilter] = useState(searchParams.get('filter') || 'all');
  const [employeeFilter, setEmployeeFilter] = useState(searchParams.get('employee_id') || 'all');
  const { token, isAuditor, loading: authLoading } = useAuth();

  // Sync filter state to URL - keep deep links stable for dashboard drill-down
  useEffect(() => {
    const currentFilter = searchParams.get('filter') || 'all';
    const currentEmployee = searchParams.get('employee_id') || 'all';

    // Only update URL if state differs from URL
    if (currentFilter !== filter || currentEmployee !== employeeFilter) {
      const newParams = new URLSearchParams(searchParams);
      if (filter && filter !== 'all') {
        newParams.set('filter', filter);
      } else {
        newParams.delete('filter');
      }
      if (employeeFilter && employeeFilter !== 'all') {
        newParams.set('employee_id', employeeFilter);
      } else {
        newParams.delete('employee_id');
      }
      setSearchParams(newParams, { replace: true });
    }
  }, [filter, employeeFilter, searchParams, setSearchParams]);


  // Correction modal state
  const [editOpen, setEditOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [correction, setCorrection] = useState({ field: 'expiry_date', new_value: '', reason: '' });
  
  // History modal state
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyRecord, setHistoryRecord] = useState(null);
  const [historyData, setHistoryData] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  
  // Export state
  const [exporting, setExporting] = useState(false);
  
  // Summary data from backend (single source of truth)
  const [matrixSummary, setMatrixSummary] = useState(null);
  const [pendingExtractions, setPendingExtractions] = useState([]);
  const [showExtractionPanel, setShowExtractionPanel] = useState(false);
  // A staff-facing page shows the next task first. Specialist tools are opt-in.
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showFilters, setShowFilters] = useState(() =>
    Boolean(searchParams.get('employee_id')) ||
    !['all', 'action_needed'].includes(searchParams.get('filter') || 'all')
  );
  
  // Extraction review modal
  const [extractionReviewOpen, setExtractionReviewOpen] = useState(false);
  const [reviewingDocumentId, setReviewingDocumentId] = useState(null);

  const [newRecord, setNewRecord] = useState({
    employee_id: '',
    training_name: '',
    mandatory: true,
    status: 'not_started',
    expiry_date: ''
  });

  const fetchData = useCallback(async () => {
    if (!token) {
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      setLoadError(false);
      const headers = { Authorization: `Bearer ${token}` };
      
      // The workforce matrix is the authority for who belongs on this page.
      // Do not fetch a second employee list: that can drift from Ready-to-Work
      // eligibility and previously caused a false "Failed to load" toast when
      // the old employee-list route changed.
      const [trainingResult, summaryResult, matrixResult, extractionsResult, defsResult] = await Promise.allSettled([
        axios.get(`${API}/training-records`, { headers }),
        axios.get(`${API}/training/matrix/summary`, { headers }),
        axios.get(`${API}/training/matrix`, { headers }),
        axios.get(`${API}/document-extractions/pending-review?limit=50`, { headers }),
        axios.get(`${API}/training/definitions`, { headers })
      ]);

      if (matrixResult.status !== 'fulfilled') {
        throw matrixResult.reason || new Error('Training matrix request failed');
      }

      const matrixData = matrixResult.value?.data || { rows: [], columns: [], summary: {} };
      const matrixRows = Array.isArray(matrixData.rows) ? matrixData.rows : [];
      const matrixEmployeeIds = new Set(
        matrixRows.map((row) => row.employee_id).filter(Boolean)
      );

      // The selector and detail audit list are restricted to the same people
      // represented by the canonical workforce matrix. If there are no
      // Ready-to-Work workers, both remain empty by design.
      setEmployees(matrixRows.map((row) => ({
        id: row.employee_id,
        first_name: row.first_name || String(row.name || '').trim().split(/\s+/)[0] || '',
        last_name: row.last_name || String(row.name || '').trim().split(/\s+/).slice(1).join(' '),
        employee_code: row.employee_code,
        role: row.role,
      })));

      const allTraining = trainingResult.status === 'fulfilled'
        ? (trainingResult.value?.data || [])
        : [];
      setTraining(
        allTraining.filter((record) => matrixEmployeeIds.has(record.employee_id))
      );
      setMatrixModel(matrixData);
      setPartialDataUnavailable(trainingResult.status !== 'fulfilled' || extractionsResult.status !== 'fulfilled');

      if (summaryResult.status === 'fulfilled' && summaryResult.value?.data) {
        setMatrixSummary(summaryResult.value.data);
      } else {
        setMatrixSummary(null);
        console.warn('[TrainingPage] Matrix summary unavailable; using matrix/read-model fallbacks');
      }

      if (defsResult.status === 'fulfilled' && defsResult.value?.data?.definitions) {
        setTrainingDefinitions(defsResult.value.data.definitions);
      }

      if (extractionsResult.status === 'fulfilled' && extractionsResult.value?.data?.extractions) {
        const trainingExtractions = extractionsResult.value.data.extractions.filter(
          (ext) =>
            ext.document_type === 'training_certificate' &&
            matrixEmployeeIds.has(ext.employee_id)
        );
        setPendingExtractions(trainingExtractions);
      } else {
        setPendingExtractions([]);
      }

      if (trainingResult.status !== 'fulfilled') {
        console.warn('[TrainingPage] Training record detail unavailable:', trainingResult.reason);
      }
    } catch (error) {
      setLoadError(true);
      setPartialDataUnavailable(false);
      console.error('Failed to fetch training data:', error);
      toast.error('Could not load the training matrix. Please retry.');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    // Only fetch data once when auth is complete and token is available
    if (!authLoading && token && !hasFetched.current) {
      hasFetched.current = true;
      fetchData();
    }
  }, [token, authLoading, fetchData]);

  const handleAddTraining = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const payload = { ...newRecord };
      if (!payload.expiry_date) delete payload.expiry_date;
      
      await axios.post(`${API}/training-records`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Training record added');
      setAddOpen(false);
      setNewRecord({ employee_id: '', training_name: '', mandatory: true, status: 'not_started', expiry_date: '' });
      hasFetched.current = false; // Allow refetch after mutation
      await fetchData();
    } catch (error) {
      toast.error('Failed to add training record');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCorrection = async () => {
    if (!correction.reason || correction.reason.trim().length < 3) {
      toast.error('Please provide a reason for this correction (minimum 3 characters)');
      return;
    }
    if (!correction.new_value) {
      toast.error('Please enter the new value');
      return;
    }
    
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/training-records/${editingRecord.id}/correct`, {
        field: correction.field,
        old_value: editingRecord[correction.field],
        new_value: correction.new_value,
        reason: correction.reason.trim()
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Training record corrected');
      setEditOpen(false);
      setEditingRecord(null);
      setCorrection({ field: 'expiry_date', new_value: '', reason: '' });
      hasFetched.current = false; // Allow refetch after mutation
      await fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to correct training record');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleViewHistory = async (record) => {
    setHistoryRecord(record);
    setHistoryOpen(true);
    setHistoryLoading(true);
    
    try {
      const res = await axios.get(`${API}/training-records/${record.id}/history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHistoryData(res.data.history || []);
    } catch (error) {
      toast.error('Failed to load history');
      setHistoryData([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  const openEditModal = (record, field = 'expiry_date') => {
    setEditingRecord(record);
    setCorrection({ 
      field, 
      new_value: record[field] || '', 
      reason: '' 
    });
    setEditOpen(true);
  };

  const getEmployeeName = (employeeId) => {
    const emp = employees.find(e => e.id === employeeId);
    return emp ? `${emp.first_name} ${emp.last_name}` : null;
  };

  // Export training matrix - uses existing backend endpoint
  const handleExport = async (format) => {
    setExporting(true);
    try {
      const response = await axios.get(`${API}/training-matrix/export`, {
        params: { format },
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      // Create download link
      const blob = new Blob([response.data], { 
        type: format === 'csv' ? 'text/csv' : 'application/pdf' 
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Extract filename from response headers or generate default
      const contentDisposition = response.headers['content-disposition'];
      let filename = `training_matrix.${format}`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename=([^;]+)/);
        if (filenameMatch) {
          filename = filenameMatch[1].replace(/"/g, '');
        }
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success(`Training matrix exported as ${format.toUpperCase()}`);
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Failed to export training matrix');
    } finally {
      setExporting(false);
    }
  };

  // Filter training records using backend-computed status
  const filteredTraining = training.filter(record => {
    if (employeeFilter !== 'all' && record.employee_id !== employeeFilter) return false;
    if (filter === 'all') return true;

    const renewalStatus = record.renewal_status;   // expiring_soon | expired | valid | no_expiry | null
    const computedStatus = record.computed_status; // needs_renewal | expired | completed | not_started

    if (filter === 'expired') {
      return renewalStatus === 'expired' || computedStatus === 'expired';
    }
    if (filter === 'expiring_soon') {
      return renewalStatus === 'expiring_soon' || computedStatus === 'needs_renewal';
    }
    if (filter === 'awaiting_review') {
      return !record.verified || record.status === 'awaiting_extraction_review' || record.verification_status === 'awaiting_review';
    }
    if (filter === 'action_needed') {
      return (
        renewalStatus === 'expired' ||
        computedStatus === 'expired' ||
        renewalStatus === 'expiring_soon' ||
        computedStatus === 'needs_renewal' ||
        !record.verified ||
        record.status === 'awaiting_extraction_review' ||
        record.verification_status === 'awaiting_review'
      );
    }
    if (filter === 'missing') {
      return false;
    }
    if (filter === 'valid') {
      return renewalStatus === 'valid' || renewalStatus === 'no_expiry' || computedStatus === 'completed';
    }
    return false;
  });

  // Use backend summary as source of truth, fallback to local calculation
  const completed = matrixSummary?.completed ?? training.filter(t => t.status === 'completed' || t.computed_status === 'completed').length;
  const expired = matrixSummary?.expired ?? training.filter(t => t.renewal_status === 'expired' || t.computed_status === 'expired').length;
  const expiringSoon = matrixSummary?.needs_renewal ?? training.filter(t => t.renewal_status === 'expiring_soon' || t.computed_status === 'needs_renewal').length;
  const verified = matrixSummary?.verified ?? training.filter(t => t.verified).length;
  const awaitingExtractionReview = matrixSummary?.awaiting_extraction_review ?? pendingExtractions.length;
  const activeEmployeeCount = matrixModel?.summary?.total_employees ?? employees.length;
  const missingRequired = matrixModel?.summary?.missing ?? 0;
  const awaitingVerification = matrixModel?.summary?.awaiting_verification ?? 0;
  const inductionIncomplete = matrixModel?.summary?.induction_incomplete ?? 0;
  const inductionManagerSignoff = matrixModel?.summary?.induction_manager_signoff_needed ?? 0;
  const inductionWorkerResubmission = matrixModel?.summary?.induction_worker_resubmission_needed ?? 0;
  const inductionTemporaryExpiring = matrixModel?.summary?.induction_temporary_expiring ?? 0;
  const inductionActionsTotal = inductionIncomplete + inductionManagerSignoff + inductionWorkerResubmission + inductionTemporaryExpiring;

  const matrixColumnsById = (matrixModel?.columns || []).reduce((acc, col) => {
    acc[col.id] = col;
    return acc;
  }, {});

  const actionItems = (matrixModel?.rows || []).flatMap((row) => {
    const trainingIssues = Object.entries(row.training || {}).flatMap(([trainingId, item]) => {
      if (!item || item.status === 'not_applicable' || item.status === 'in_date') return [];

      const column = matrixColumnsById[trainingId] || {};
      const status = item.status;
      const isMissing = status === 'missing';
      const isExpired = status === 'expired';
      const isExpiring = status === 'expiring';
      const isAwaitingVerification = status === 'completed' || item.verification_status === 'awaiting_review';
      const isTemporaryInduction = Boolean(item.is_temporary_induction_evidence);

      if (!isMissing && !isExpired && !isExpiring && !isAwaitingVerification) return [];

      const priority =
        isExpired ? 1 :
        isTemporaryInduction ? 2 :
        isMissing ? 3 :
        isAwaitingVerification ? 3 :
        4;

      const label =
        isTemporaryInduction && isExpired ? 'Induction evidence expired' :
        isTemporaryInduction ? 'Temporary induction evidence' :
        isExpired ? 'Expired' :
        isMissing ? 'Missing' :
        isAwaitingVerification ? 'Awaiting verification' :
        'Due soon';

      const nextAction =
        isTemporaryInduction ? 'Monitor worker upload' :
        isExpired ? 'Check worker status' :
        isMissing ? 'Check worker status' :
        isAwaitingVerification ? 'Verify certificate' :
        'Track renewal';

      return [{
        id: `${row.employee_id}-${trainingId}`,
        priority,
        employeeId: row.employee_id,
        employeeName: row.name,
        employeeCode: row.employee_code,
        role: row.role,
        trainingId,
        trainingName: column.name || column.label || trainingId.replace(/_/g, ' '),
        status,
        label,
        nextAction,
        expiryDate: item.expiry_date,
        daysUntilExpiry: item.days_until_expiry,
        provider: item.provider,
        tone: isExpired ? 'red' : isMissing ? 'slate' : isAwaitingVerification ? 'blue' : 'amber',
      }];
    });

    const induction = row.induction || {};
    const inductionIssues = [];
    if (induction.manager_signoff_count > 0) {
      inductionIssues.push({
        id: `${row.employee_id}-induction-manager-signoff`,
        priority: 2,
        employeeId: row.employee_id,
        employeeName: row.name,
        employeeCode: row.employee_code,
        role: row.role,
        trainingName: `${induction.manager_signoff_count} induction item${induction.manager_signoff_count === 1 ? '' : 's'}`,
        status: 'induction_manager_signoff',
        label: 'Manager sign-off needed',
        nextAction: 'Review induction',
        expiryDate: null,
        daysUntilExpiry: null,
        provider: induction.pending_items?.[0]?.title,
        tone: 'blue',
      });
    }
    if (induction.worker_resubmission_count > 0) {
      inductionIssues.push({
        id: `${row.employee_id}-induction-worker-resubmission`,
        priority: 3,
        employeeId: row.employee_id,
        employeeName: row.name,
        employeeCode: row.employee_code,
        role: row.role,
        trainingName: `${induction.worker_resubmission_count} induction correction${induction.worker_resubmission_count === 1 ? '' : 's'}`,
        status: 'induction_worker_resubmission',
        label: 'Worker resubmission',
        nextAction: 'Open induction',
        expiryDate: null,
        daysUntilExpiry: null,
        provider: induction.pending_items?.[0]?.title,
        tone: 'amber',
      });
    }
    if (induction.overall_status && induction.overall_status !== 'completed' && induction.manager_signoff_count === 0 && induction.worker_resubmission_count === 0) {
      inductionIssues.push({
        id: `${row.employee_id}-induction-incomplete`,
        priority: 4,
        employeeId: row.employee_id,
        employeeName: row.name,
        employeeCode: row.employee_code,
        role: row.role,
        trainingName: `Induction ${induction.completed || 0}/${induction.total || 0}`,
        status: 'induction_incomplete',
        label: 'Induction incomplete',
        nextAction: 'Open induction',
        expiryDate: null,
        daysUntilExpiry: null,
        provider: induction.pending_items?.[0]?.title || induction.next_action,
        tone: 'slate',
      });
    }

    const pendingExtractionCount = Number(row.pending_extraction_count || 0);
    const extractionIssue = pendingExtractionCount > 0 ? [{
      id: `${row.employee_id}-pending-extraction`,
      priority: 3,
      employeeId: row.employee_id,
      employeeName: row.name,
      employeeCode: row.employee_code,
      role: row.role,
      trainingName: `${pendingExtractionCount} certificate${pendingExtractionCount === 1 ? '' : 's'}`,
      status: 'awaiting_extraction_review',
      label: 'Extraction review',
      nextAction: 'Review upload',
      tone: 'blue',
    }] : [];

    return [...trainingIssues, ...inductionIssues, ...extractionIssue];
  }).sort((a, b) => a.priority - b.priority || a.employeeName.localeCompare(b.employeeName));

  const filteredActionItems = actionItems.filter((item) => {
    if (employeeFilter !== 'all' && item.employeeId !== employeeFilter) return false;
    if (filter === 'all' || filter === 'action_needed') return true;
    if (filter === 'expired') return item.status === 'expired';
    if (filter === 'expiring_soon') return item.status === 'expiring';
    if (filter === 'missing') return item.status === 'missing';
    if (filter === 'awaiting_review') return item.status === 'completed' || item.status === 'awaiting_extraction_review';
    if (filter === 'induction') return String(item.status || '').startsWith('induction_') || item.label?.toLowerCase().includes('induction');
    if (filter === 'valid') return false; // Valid records belong in the detailed record list, never the action queue.
    return true;
  });

  const openEmployeeTraining = (employeeId) => {
    if (!employeeId) return;
    navigate(`/portal/employees/${employeeId}?tab=training`);
  };

  // Handle extraction review
  const handleOpenExtractionReview = (documentId) => {
    setReviewingDocumentId(documentId);
    setExtractionReviewOpen(true);
  };

  const handleExtractionReviewComplete = () => {
    setExtractionReviewOpen(false);
    setReviewingDocumentId(null);
    hasFetched.current = false; // Allow refetch after mutation
    fetchData(); // Refresh to update counts
  };

  return (
    <div className="space-y-6" data-testid="training-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-text-primary">
            Training
          </h1>
          <p className="text-text-muted mt-1">
            See who needs support, open their training, and track what is still due.
          </p>
        </div>
        
        {showAdvanced && (
        <div className="flex items-center gap-2">
          {/* Export Dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button 
                variant="outline" 
                className="rounded-xl" 
                disabled={exporting}
                data-testid="export-matrix-btn"
              >
                {exporting ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Download className="mr-2 h-4 w-4" />
                )}
                Export
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => handleExport('csv')} data-testid="export-csv">
                <FileSpreadsheet className="mr-2 h-4 w-4" />
                Export as CSV
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleExport('pdf')} data-testid="export-pdf">
                <FileText className="mr-2 h-4 w-4" />
                Export as PDF
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          
          {!isAuditor() && (
            <Dialog open={addOpen} onOpenChange={setAddOpen}>
              <DialogTrigger asChild>
                <Button className="bg-primary hover:bg-primary-hover text-white rounded-xl" data-testid="add-training-btn">
                  <Plus className="mr-2 h-4 w-4" />
                  Add Training
                </Button>
              </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle className="font-heading">Add Training Record</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleAddTraining} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label>Employee *</Label>
                  <Select value={newRecord.employee_id} onValueChange={(value) => setNewRecord({...newRecord, employee_id: value})}>
                    <SelectTrigger className="rounded-xl" data-testid="training-employee">
                      <SelectValue placeholder="Select employee" />
                    </SelectTrigger>
                    <SelectContent>
                      {employees.map((emp) => (
                        <SelectItem key={emp.id} value={emp.id}>
                          {emp.first_name} {emp.last_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Training Course *</Label>
                  <Select value={newRecord.training_name} onValueChange={(value) => setNewRecord({...newRecord, training_name: value})}>
                    <SelectTrigger className="rounded-xl" data-testid="training-name">
                      <SelectValue placeholder="Select training" />
                    </SelectTrigger>
                    <SelectContent>
                      {/* Canonical training definitions from backend */}
                      {trainingDefinitions.map((d) => (
                        <SelectItem key={d.id} value={d.name}>{d.name}{d.mandatory_for_compliance ? ' *' : ''}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Expiry Date (optional)</Label>
                  <Input 
                    type="date" 
                    value={newRecord.expiry_date} 
                    onChange={(e) => setNewRecord({...newRecord, expiry_date: e.target.value})}
                    className="rounded-xl"
                    data-testid="training-expiry-date"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Status</Label>
                  <Select value={newRecord.status} onValueChange={(value) => setNewRecord({...newRecord, status: value})}>
                    <SelectTrigger className="rounded-xl" data-testid="training-status">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="not_started">Not Started</SelectItem>
                      <SelectItem value="in_progress">In Progress</SelectItem>
                      <SelectItem value="completed">Completed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setAddOpen(false)} className="rounded-xl">
                    Cancel
                  </Button>
                  <Button type="submit" disabled={isSubmitting} className="bg-primary hover:bg-primary-hover text-white rounded-xl" data-testid="training-submit">
                    {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Add Record'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
          )}
        </div>
        )}
      </div>

      {/* Plain-language triage; source counts and priority still come from the workforce matrix. */}
      {loadError ? (
        <div role="alert" className="rounded-2xl border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900" data-testid="training-load-error">
          <p className="font-semibold">We couldn't load the training list.</p>
          <p className="mt-1">Training status cannot be confirmed until the list is available. No records have been changed.</p>
          <Button type="button" variant="outline" size="sm" onClick={fetchData} className="mt-3">Retry loading</Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4" data-testid="training-simple-summary">
          <div className="rounded-xl border border-slate-200 bg-white p-3">
            <p className="text-sm text-slate-600">Staff in training list</p>
            <p className="mt-1 text-2xl font-semibold text-slate-900">{loading ? '–' : activeEmployeeCount}</p>
          </div>
          <div className="rounded-xl border border-slate-200 bg-white p-3">
            <p className="text-sm text-slate-600">Items to sort out</p>
            <p className="mt-1 text-2xl font-semibold text-slate-900">{loading ? '–' : actionItems.length}</p>
          </div>
          <div className="rounded-xl border border-red-200 bg-white p-3">
            <p className="text-sm text-slate-600">Expired training</p>
            <p className="mt-1 text-2xl font-semibold text-red-700">{loading ? '–' : expired}</p>
          </div>
          <div className="rounded-xl border border-amber-200 bg-white p-3">
            <p className="text-sm text-slate-600">Renewals due soon</p>
            <p className="mt-1 text-2xl font-semibold text-amber-800">{loading ? '–' : expiringSoon}</p>
          </div>
        </div>
      )}

      {!loadError && !loading && partialDataUnavailable && (
        <div role="status" className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900" data-testid="training-partial-data-warning">
          Some certificate or record details could not be loaded. The workforce task list may still be used, but check the staff file before approving evidence.
          <button type="button" className="ml-2 font-semibold underline" onClick={fetchData}>Refresh details</button>
        </div>
      )}

      {/* Read-only scans continue even when detailed reports are closed.
          Compact exceptions remain visible so a calm UI cannot hide problems. */}
      {!isAuditor() && (
        <div className="space-y-2" data-testid="training-background-checks">
          <TrainingIntegrityMonitor compact={!showAdvanced} onShowDetails={() => setShowAdvanced(true)} />
          <TrainingHubQueueSummary compact={!showAdvanced} onShowDetails={() => setShowAdvanced(true)} />
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-2">
        <button type="button" onClick={() => setShowFilters(value => !value)}
          aria-expanded={showFilters} data-testid="training-filter-toggle"
          className="min-h-11 rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-800 hover:bg-slate-50">
          <Filter className="mr-2 inline-block h-4 w-4" />
          {showFilters ? 'Hide filters' : 'Filter tasks'}
        </button>
        <button type="button" onClick={() => setShowAdvanced(value => !value)}
          aria-expanded={showAdvanced} data-testid="training-advanced-toggle"
          className="min-h-11 rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-800 hover:bg-slate-50">
          {showAdvanced ? 'Hide records & checks' : 'Records, reports & checks'}
        </button>
      </div>
      {showFilters && (
        <>
      {/* Filters */}
      <Card className="border-[#E4E8EB] shadow-sm">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-3">
            <Filter className="h-4 w-4 text-text-muted" />
            <span className="text-sm text-text-muted">Filter:</span>
            <div className="flex flex-wrap gap-2">
              <Button 
                variant={filter === 'action_needed' ? 'default' : 'outline'} 
                size="sm" 
                className="rounded-xl"
                onClick={() => setFilter('action_needed')}
                data-testid="filter-action-needed"
              >
                Action Needed ({actionItems.length})
              </Button>
              <Button 
                variant={filter === 'all' ? 'default' : 'outline'} 
                size="sm" 
                className="rounded-xl"
                onClick={() => setFilter('all')}
                data-testid="filter-all"
              >
                All tasks
              </Button>
              <Button 
                variant={filter === 'missing' ? 'default' : 'outline'} 
                size="sm" 
                className={`rounded-xl ${filter === 'missing' ? 'bg-slate-700 hover:bg-slate-800' : 'text-slate-700 border-slate-200 hover:bg-slate-50'}`}
                onClick={() => setFilter('missing')}
                data-testid="filter-missing"
              >
                Missing ({missingRequired})
              </Button>
              <Button 
                variant={filter === 'expired' ? 'default' : 'outline'} 
                size="sm" 
                className={`rounded-xl ${filter === 'expired' ? 'bg-red-600 hover:bg-red-700' : 'text-red-600 border-red-200 hover:bg-red-50'}`}
                onClick={() => setFilter('expired')}
                data-testid="filter-expired"
              >
                Expired ({expired})
              </Button>
              <Button 
                variant={filter === 'expiring_soon' ? 'default' : 'outline'} 
                size="sm" 
                className={`rounded-xl ${filter === 'expiring_soon' ? 'bg-amber-600 hover:bg-amber-700' : 'text-amber-600 border-amber-200 hover:bg-amber-50'}`}
                onClick={() => setFilter('expiring_soon')}
                data-testid="filter-expiring"
              >
                Needs Renewal ({expiringSoon})
              </Button>
              <Button 
                variant={filter === 'awaiting_review' ? 'default' : 'outline'} 
                size="sm" 
                className={`rounded-xl ${filter === 'awaiting_review' ? 'bg-blue-600 hover:bg-blue-700' : 'text-blue-600 border-blue-200 hover:bg-blue-50'}`}
                onClick={() => setFilter('awaiting_review')}
                data-testid="filter-awaiting-review"
              >
                To Review ({awaitingVerification + awaitingExtractionReview})
              </Button>
              <Button 
                variant={filter === 'induction' ? 'default' : 'outline'} 
                size="sm" 
                className={`rounded-xl ${filter === 'induction' ? 'bg-indigo-600 hover:bg-indigo-700' : 'text-indigo-600 border-indigo-200 hover:bg-indigo-50'}`}
                onClick={() => setFilter('induction')}
                data-testid="filter-induction"
              >
                Induction ({inductionActionsTotal})
              </Button>
              <Button 
                variant={filter === 'valid' ? 'default' : 'outline'} 
                size="sm" 
                className={`rounded-xl ${filter === 'valid' ? 'bg-green-600 hover:bg-green-700' : 'text-green-600 border-green-200 hover:bg-green-50'}`}
                onClick={() => setFilter('valid')}
                data-testid="filter-valid"
              >
                Valid
              </Button>
            </div>
            <Select value={employeeFilter} onValueChange={setEmployeeFilter}>
              <SelectTrigger className="w-full sm:w-72 rounded-xl" data-testid="filter-employee">
                <SelectValue placeholder="Filter by employee" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All employees</SelectItem>
                {employees.map((emp) => (
                  <SelectItem key={emp.id} value={emp.id}>
                    {emp.first_name} {emp.last_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>


        </>
      )}
      {!loadError && (
      <Card className="border-[#E4E8EB] shadow-sm overflow-hidden" data-testid="training-control-centre">
        <CardHeader className="pb-3">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <ListChecks className="h-5 w-5 text-primary" />
                Action Needed
              </CardTitle>
              <p className="text-sm text-text-muted mt-1">
                Start with the most urgent item. Choose a task to open that person's training.
              </p>
            </div>
            {(awaitingVerification + awaitingExtractionReview + inductionActionsTotal) > 0 && (
              <span className="text-sm font-medium text-slate-600">
                {awaitingVerification + awaitingExtractionReview} to review · {inductionActionsTotal} induction follow-ups
              </span>
            )}
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : filteredActionItems.length === 0 ? (
            <div className="px-6 py-10 text-center text-text-muted">
              <CheckCircle className="h-10 w-10 mx-auto mb-3 text-green-600" />
              <p className="font-medium text-text-primary">
                {activeEmployeeCount === 0 ? 'No Ready-to-Work employees yet' : filter === 'valid' ? 'Current training is in the full records list' : 'No action needed for this view'}
              </p>
              <p className="text-sm mt-1">
                {activeEmployeeCount === 0
                  ? 'This matrix intentionally starts when a worker reaches Ready to Work.'
                  : filter === 'valid'
                    ? 'Open Records, reports & checks to review current training entries.'
                    : 'Ready-to-Work staff training is clear for the selected filter.'}
              </p>
            </div>
          ) : (
            <div className="divide-y divide-[#E4E8EB]">
              {filteredActionItems.slice(0, 18).map((item) => (
                <div key={item.id} className="grid gap-3 px-4 py-3 hover:bg-[#F8FAFA] md:grid-cols-[minmax(160px,1.2fr)_minmax(180px,1.3fr)_140px_130px] md:items-center">
                  <div className="min-w-0">
                    <p className="font-medium text-text-primary truncate">{item.employeeName}</p>
                    <p className="text-xs text-text-muted truncate">{item.employeeCode || item.role || 'Active employee'}</p>
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-text-primary truncate">{item.trainingName}</p>
                    <p className="text-xs text-text-muted">
                      {item.expiryDate ? `Expires ${formatBackendDate(item.expiryDate, { format: 'medium' })}` : item.provider || 'Required training item'}
                      {typeof item.daysUntilExpiry === 'number' ? ` · ${item.daysUntilExpiry < 0 ? `${Math.abs(item.daysUntilExpiry)} days overdue` : `${item.daysUntilExpiry} days left`}` : ''}
                    </p>
                  </div>
                  <div>
                    <span className={`inline-flex rounded-full px-3 py-1 text-xs font-medium ${
                      item.tone === 'red' ? 'bg-red-100 text-red-700' :
                      item.tone === 'amber' ? 'bg-amber-100 text-amber-700' :
                      item.tone === 'blue' ? 'bg-blue-100 text-blue-700' :
                      'bg-slate-100 text-slate-700'
                    }`}>
                      {item.label}
                    </span>
                  </div>
                  <div className="flex gap-2 md:justify-end">
                    {item.status === 'awaiting_extraction_review' ? (
                      <Button
                        size="sm"
                        variant="outline"
                        className="rounded-xl border-blue-200 text-blue-700 hover:bg-blue-50"
                        onClick={() => {
                          const pending = pendingExtractions.find(ext =>
                            ext.employee_id === item.employeeId && ext.document_id);
                          if (!isAuditor() && pending) handleOpenExtractionReview(pending.document_id);
                          else openEmployeeTraining(item.employeeId);
                        }}
                      >
                        <FileSearch className="mr-1 h-4 w-4" />
                        Review
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        variant="outline"
                        className="rounded-xl"
                        onClick={() => openEmployeeTraining(item.employeeId)}
                      >
                        <ArrowRight className="mr-1 h-4 w-4" />
                        {item.nextAction}
                      </Button>
                    )}
                  </div>
                </div>
              ))}
              {filteredActionItems.length > 18 && (
                <div className="px-4 py-3 text-sm text-text-muted">
                  Showing the first 18 highest-priority items. Use filters or employee search to narrow the list.
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      )}

      {/* Pending Extraction Review Panel - Shown when card is clicked */}
      {showExtractionPanel && pendingExtractions.length > 0 && (
        <Card className="border-blue-200 bg-blue-50/50 shadow-sm" data-testid="extraction-review-panel">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <FileSearch className="h-5 w-5 text-blue-600" />
                Training Certificates Awaiting Extraction Review
              </CardTitle>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => setShowExtractionPanel(false)}
                className="text-blue-600 hover:text-blue-800"
              >
                Hide
              </Button>
            </div>
            <p className="text-sm text-text-muted">
              Check the information taken from each certificate before accepting it as training evidence.
            </p>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {pendingExtractions.map((ext) => (
                <div 
                  key={ext.id}
                  className="flex items-center justify-between p-3 bg-white rounded-lg border border-blue-200 hover:border-blue-400 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <FileText className="h-5 w-5 text-blue-600" />
                    <div>
                      <p className="font-medium text-text-primary">{ext.document_file_name || 'Training Certificate'}</p>
                      <p className="text-sm text-text-muted">
                        {ext.employee_name || 'Unknown Employee'} • Extracted: {ext.extracted_at ? new Date(ext.extracted_at).toLocaleDateString() : 'N/A'}
                      </p>
                      {ext.extracted_fields?.training_title && (
                        <p className="text-xs text-blue-600 mt-1">
                          Training: {ext.extracted_fields.training_title}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {ext.confidence_score && (
                      <span className={`text-xs px-2 py-1 rounded ${
                        ext.confidence_score >= 0.8 ? 'bg-green-100 text-green-700' :
                        ext.confidence_score >= 0.5 ? 'bg-amber-100 text-amber-700' :
                        'bg-red-100 text-red-700'
                      }`}>
                        {Math.round(ext.confidence_score * 100)}% confidence
                      </span>
                    )}
                    <Button
                      size="sm"
                      variant="outline"
                      className="rounded-xl border-blue-300 text-blue-700 hover:bg-blue-100"
                      onClick={() => handleOpenExtractionReview(ext.document_id)}
                      data-testid={`review-extraction-${ext.document_id}`}
                    >
                      <Eye className="h-4 w-4 mr-1" />
                      Review
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {showAdvanced && (
        <div className="space-y-4" data-testid="training-advanced-workspace">
          {!isAuditor() && pendingExtractions.length > 0 && (
            <Button type="button" variant="outline" onClick={() => setShowExtractionPanel(value => !value)}
              data-testid="training-review-certificate-batch">
              {showExtractionPanel ? 'Hide uploaded certificates' : `Review uploaded certificates (${pendingExtractions.length})`}
            </Button>
          )}
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700">
            <p className="font-semibold text-slate-900">Records and specialist checks</p>
            <p className="mt-1">Use this area for audit history, record corrections, exports and training integrity checks. Training is only considered verified after the evidence review.</p>
            <p className="mt-2">{verified} verified records · {completed} completed entries · {awaitingVerification + awaitingExtractionReview} awaiting review · {inductionActionsTotal} induction follow-ups</p>
          </div>
          {/* Training Records Table */}
      <Card className="border-[#E4E8EB] shadow-sm">
        <CardHeader>
          <CardTitle className="font-heading text-lg">Training Record Detail</CardTitle>
          <p className="text-sm text-text-muted">
            Use this audit list for corrections, history, and individual certificate records.
          </p>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : filteredTraining.length === 0 ? (
            <div className="text-center py-12 text-text-muted">
              <GraduationCap className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>
                {employeeFilter !== 'all'
                  ? 'No training records for this employee and filter'
                  : (activeEmployeeCount === 0
                    ? 'No Ready-to-Work employee training records yet'
                    : (filter === 'all' ? 'No training records yet' : `No ${filter.replace('_', ' ')} training records`))}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-[#E4E8EB] bg-[#F8FAFA]">
                    <th className="text-left p-4 font-medium text-text-muted text-sm">Employee</th>
                    <th className="text-left p-4 font-medium text-text-muted text-sm">Training</th>
                    <th className="text-left p-4 font-medium text-text-muted text-sm">Status</th>
                    <th className="text-left p-4 font-medium text-text-muted text-sm">Expiry Date</th>
                    <th className="text-left p-4 font-medium text-text-muted text-sm">Renewal Status</th>
                    <th className="text-left p-4 font-medium text-text-muted text-sm">Verified</th>
                    <th className="text-left p-4 font-medium text-text-muted text-sm w-12">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTraining.map((record) => {
                    // Use backend-computed status - SINGLE SOURCE OF TRUTH
                    const expiryStatus = getBackendExpiryStatus(record);
                    
                    return (
                      <tr key={record.id} className="border-b border-[#E4E8EB] hover:bg-[#F8FAFA]" data-testid={`training-row-${record.id}`}>
                        <td className="p-4 font-medium text-text-primary">
                          {record.employee_name || getEmployeeName(record.employee_id) || record.employee_id || 'Unknown'}
                        </td>
                        <td className="p-4 text-text-primary">
                          <div>
                            {record.training_name}
                            {record.mandatory && (
                              <span className="ml-2 text-xs text-red-600 font-medium">Mandatory</span>
                            )}
                          </div>
                        </td>
                        <td className="p-4">
                          {/* UI INTEGRITY: Status must include verification state and intake workflow states */}
                          <span className={`px-2 py-1 rounded-lg text-xs font-medium ${
                            record.status === 'completed' && record.verified ? 'bg-green-100 text-green-700' :
                            record.status === 'completed' ? 'bg-blue-100 text-blue-700' :
                            record.status === 'awaiting_extraction_review' ? 'bg-purple-100 text-purple-700' :
                            record.status === 'awaiting_evidence' || record.status === 'evidence_requested' ? 'bg-amber-100 text-amber-700' :
                            record.status === 'in_progress' ? 'bg-amber-100 text-amber-700' :
                            'bg-gray-100 text-gray-700'
                          }`}>
                            {record.status === 'completed' && record.verified ? 'Completed & Verified' :
                             record.status === 'completed' ? 'Completed (Awaiting Verification)' :
                             record.status === 'awaiting_extraction_review' ? 'Awaiting Extraction Review' :
                             record.status === 'awaiting_evidence' || record.status === 'evidence_requested' ? 'Evidence Requested' :
                             record.status === 'in_progress' ? 'In Progress' :
                             'Not Started'}
                          </span>
                        </td>
                        <td className="p-4 text-text-muted text-sm">
                          {formatBackendDate(record.expiry_date, { format: 'medium', fallback: '-' })}
                        </td>
                        <td className="p-4">
                          {expiryStatus ? (
                            <div className="flex flex-col">
                              <span className={`px-2 py-1 rounded-lg text-xs font-medium inline-block w-fit ${
                                expiryStatus.color === 'red' ? 'bg-red-100 text-red-700' :
                                expiryStatus.color === 'amber' ? 'bg-amber-100 text-amber-700' :
                                'bg-green-100 text-green-700'
                              }`}>
                                {expiryStatus.label}
                              </span>
                              <span className="text-xs text-text-muted mt-1">{expiryStatus.daysText}</span>
                            </div>
                          ) : (
                            <span className="text-xs text-text-muted">No expiry set</span>
                          )}
                        </td>
                        <td className="p-4">
                          {record.verified ? (
                            <div className="flex items-center gap-1">
                              <ShieldCheck className="h-4 w-4 text-green-600" />
                              <span className="text-xs text-green-600">Yes</span>
                            </div>
                          ) : (
                            <span className="text-xs text-text-muted">No</span>
                          )}
                        </td>
                        <td className="p-4">
                          {!isAuditor() && (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="sm" className="h-8 w-8 p-0" data-testid={`training-actions-${record.id}`}>
                                  <MoreVertical className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => openEditModal(record, 'expiry_date')}>
                                  <Edit className="mr-2 h-4 w-4" />
                                  Edit Expiry Date
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => openEditModal(record, 'completion_date')}>
                                  <Edit className="mr-2 h-4 w-4" />
                                  Edit Completion Date
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => openEditModal(record, 'status')}>
                                  <Edit className="mr-2 h-4 w-4" />
                                  Edit Status
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleViewHistory(record)}>
                                  <History className="mr-2 h-4 w-4" />
                                  View History
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
        </div>
      )}

      {/* Edit/Correction Modal */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading">Edit Training Record</DialogTitle>
            <DialogDescription>
              Make a correction to this training record. All changes require a reason and are logged for audit purposes.
            </DialogDescription>
          </DialogHeader>
          {editingRecord && (
            <div className="space-y-4 mt-4">
              <div className="p-3 bg-[#F8FAFA] rounded-lg border border-[#E4E8EB]">
                <p className="font-medium text-text-primary">{editingRecord.training_name}</p>
                <p className="text-sm text-text-muted">{getEmployeeName(editingRecord.employee_id)}</p>
              </div>
              
              <div className="space-y-2">
                <Label>Field to Edit</Label>
                <Select value={correction.field} onValueChange={(value) => setCorrection({...correction, field: value, new_value: editingRecord[value] || ''})}>
                  <SelectTrigger className="rounded-xl">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="expiry_date">Expiry Date</SelectItem>
                    <SelectItem value="completion_date">Completion Date</SelectItem>
                    <SelectItem value="status">Status</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Current Value</Label>
                <Input 
                  value={editingRecord[correction.field] || '(not set)'} 
                  disabled 
                  className="rounded-xl bg-gray-100"
                />
              </div>
              
              <div className="space-y-2">
                <Label>New Value *</Label>
                {correction.field === 'status' ? (
                  <Select value={correction.new_value} onValueChange={(value) => setCorrection({...correction, new_value: value})}>
                    <SelectTrigger className="rounded-xl" data-testid="correction-new-value">
                      <SelectValue placeholder="Select new status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="not_started">Not Started</SelectItem>
                      <SelectItem value="in_progress">In Progress</SelectItem>
                      <SelectItem value="completed">Completed</SelectItem>
                      <SelectItem value="expired">Expired</SelectItem>
                      <SelectItem value="renewal_due">Renewal Due</SelectItem>
                    </SelectContent>
                  </Select>
                ) : (
                  <Input 
                    type="date" 
                    value={correction.new_value?.split('T')[0] || ''} 
                    onChange={(e) => setCorrection({...correction, new_value: e.target.value})}
                    className="rounded-xl"
                    data-testid="correction-new-value"
                  />
                )}
              </div>
              
              <div className="space-y-2">
                <Label>Reason for Change *</Label>
                <Textarea 
                  placeholder="Explain why this correction is being made (required for audit trail)"
                  value={correction.reason}
                  onChange={(e) => setCorrection({...correction, reason: e.target.value})}
                  className="rounded-xl min-h-[80px]"
                  data-testid="correction-reason"
                />
                <p className="text-xs text-text-muted">This will be permanently recorded in the audit log</p>
              </div>
              
              {editingRecord.verified && (
                <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                  <p className="text-sm text-amber-700">
                    <AlertTriangle className="h-4 w-4 inline mr-2" />
                    This record has been verified. Corrections will be flagged in the audit log.
                  </p>
                </div>
              )}
            </div>
          )}
          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => setEditOpen(false)} className="rounded-xl">
              Cancel
            </Button>
            <Button 
              onClick={handleCorrection} 
              disabled={isSubmitting || !correction.reason || !correction.new_value}
              className="bg-primary hover:bg-primary-hover text-white rounded-xl"
              data-testid="correction-submit"
            >
              {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save Correction'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* History Modal */}
      <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading">Training Record History</DialogTitle>
          </DialogHeader>
          {historyRecord && (
            <div className="space-y-4 mt-4">
              <div className="p-3 bg-[#F8FAFA] rounded-lg border border-[#E4E8EB]">
                <p className="font-medium text-text-primary">{historyRecord.training_name}</p>
                <p className="text-sm text-text-muted">{getEmployeeName(historyRecord.employee_id)}</p>
              </div>
              
              {historyLoading ? (
                <div className="flex items-center justify-center h-32">
                  <Loader2 className="h-6 w-6 animate-spin text-primary" />
                </div>
              ) : historyData.length === 0 ? (
                <div className="text-center py-8 text-text-muted">
                  <History className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p>No correction history</p>
                </div>
              ) : (
                <div className="space-y-3 max-h-80 overflow-y-auto">
                  {historyData.map((entry, idx) => (
                    <div key={entry.id || idx} className="p-3 bg-white rounded-lg border border-[#E4E8EB]">
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="font-medium text-text-primary">
                            {entry.action === 'training_correction' ? 'Correction' : entry.action?.replace('_', ' ')}
                          </p>
                          {entry.field_changed && (
                            <p className="text-sm text-text-muted">
                              <span className="font-medium">{entry.field_changed}</span>: {entry.old_value || '(empty)'} → {entry.new_value}
                            </p>
                          )}
                          {entry.reason && (
                            <p className="text-sm text-text-muted mt-1">
                              <span className="font-medium">Reason:</span> {entry.reason}
                            </p>
                          )}
                          {entry.was_verified_before_correction && (
                            <span className="inline-block mt-1 px-2 py-0.5 bg-amber-100 text-amber-700 text-xs rounded">
                              Corrected after verification
                            </span>
                          )}
                        </div>
                        <div className="text-right text-xs text-text-muted">
                          <p>{entry.changed_by_name || 'System'}</p>
                          <p>{entry.created_at ? formatBackendDateTime(entry.created_at) : ''}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Extraction Review Modal - Step 8 */}
      {extractionReviewOpen && reviewingDocumentId && (
        <DocumentExtractionReview
          documentId={reviewingDocumentId}
          onClose={() => setExtractionReviewOpen(false)}
          onApproved={handleExtractionReviewComplete}
          documentName="Training Certificate"
        />
      )}
    </div>
  );
}

